/**
 * ProvisionalEanPanel — Aug-2026 (User-Wunsch): aus der Warenwirtschaft in die
 * KI-Anreicherung umgezogen. Artikel mit vorläufigem EAN (= Artikelnummer)
 * werden NACHTS automatisch per KI-Websuche mit dem echten EAN angereichert —
 * hier: Restliste, manueller KI-Lauf und manuelle Erfassung.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Barcode, Loader2, Save, Sparkles, Moon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const ProvisionalEanPanel = ({ standalone = false }) => {
    const [items, setItems] = useState(null);
    const [edit, setEdit] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [aiRunning, setAiRunning] = useState(false);
    const [aiSummary, setAiSummary] = useState(null);

    const load = useCallback(() => {
        api.get("/wms/provisional-ean-articles")
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async (a) => {
        const ean = (edit[a.id] || "").trim();
        if (!/^\d{8,14}$/.test(ean)) { toast.error("Bitte gültigen EAN (8–14 Ziffern) eingeben"); return; }
        setBusyId(a.id);
        try {
            await api.patch(`/articles/${a.id}`, { ean });
            toast.success(`Echter EAN für ${a.sku} erfasst`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusyId(null);
        }
    };

    const runAi = async () => {
        setAiRunning(true);
        setAiSummary(null);
        try {
            const { data } = await api.post("/wms/provisional-ean-run", {}, { timeout: 300000 });
            setAiSummary(data);
            if (data.applied > 0) toast.success(`${data.applied} echte(r) EAN(s) per KI gefunden und übernommen`);
            else toast.info(`KI hat ${data.checked} Artikel geprüft — kein sicherer EAN-Treffer`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Lauf fehlgeschlagen");
        } finally {
            setAiRunning(false);
        }
    };

    if (items === null) {
        return standalone
            ? <div className="text-sm text-gray-500 py-6" data-testid="provisional-ean-loading">Lade EAN-Nachpflege…</div>
            : null;
    }
    if (items.length === 0) {
        if (!standalone) return null;
        return (
            <div className="bg-white border border-emerald-200 rounded-sm p-6 text-center" data-testid="provisional-ean-empty">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="font-bold text-sm text-emerald-800">Keine vorläufigen EAN-Codes offen</p>
                <p className="text-xs text-gray-500 mt-1">
                    Neue Artikel mit vorläufigem EAN werden nachts automatisch per KI-Websuche nachgepflegt.
                </p>
            </div>
        );
    }
    return (
        <div className="bg-white border border-amber-300 rounded-sm p-4 mb-6" data-testid="provisional-ean-panel">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <h2 className="font-bold text-sm uppercase tracking-wider inline-flex items-center gap-2 text-amber-800">
                    <Barcode className="w-4 h-4" /> EAN-Nachpflege ({items.length}) — vorläufige EAN-Codes ersetzen
                </h2>
                <button type="button" onClick={runAi} disabled={aiRunning}
                    className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-[#111827] hover:bg-black rounded-sm px-3 py-1.5 disabled:opacity-60"
                    data-testid="prov-ean-ai-run">
                    {aiRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-[#FFCC00]" />}
                    {aiRunning ? "KI sucht echte EANs…" : "Jetzt per KI nachschlagen"}
                </button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3 inline-flex items-center gap-1.5" data-testid="prov-ean-nightly-hint">
                <Moon className="w-3.5 h-3.5" /> Läuft zusätzlich jede Nacht automatisch — sichere Treffer werden sofort übernommen.
            </p>
            {aiSummary && (
                <p className="text-[11px] font-bold text-emerald-700 mb-2" data-testid="prov-ean-ai-summary">
                    Letzter KI-Lauf: {aiSummary.checked} geprüft · {aiSummary.applied} EAN(s) übernommen
                </p>
            )}
            <ul className="space-y-1.5">
                {items.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2 border border-gray-200 rounded-sm px-2.5 py-1.5 text-xs" data-testid={`prov-ean-row-${a.sku}`}>
                        {/* Aug-2026 (User-Wunsch): Klick öffnet den Artikel direkt in der Warenwirtschaft. */}
                        <Link
                            to={`/dealer/wms?article=${a.id}&q=${encodeURIComponent(a.sku || "")}`}
                            className="flex items-center gap-2 min-w-0 group"
                            title="Artikel in der Warenwirtschaft öffnen"
                            data-testid={`prov-ean-open-${a.sku}`}
                        >
                            <span className="font-mono font-bold text-sky-800 group-hover:underline">{a.sku}</span>
                            <span className="text-gray-600 truncate max-w-[260px] group-hover:underline">{a.name}</span>
                        </Link>
                        {a.site && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-sky-50 text-sky-800 border border-sky-200">{a.site}</span>}
                        <span className="text-gray-400">vorläufig: {a.ean}</span>
                        {a.ean_ai_note && (
                            <span className="text-[10px] text-gray-400 italic" title={a.ean_ai_checked_at || ""}>
                                KI: {a.ean_ai_note}
                            </span>
                        )}
                        <input
                            value={edit[a.id] || ""}
                            onChange={(e) => setEdit((p) => ({ ...p, [a.id]: e.target.value }))}
                            placeholder="Echten EAN scannen/eingeben"
                            className="h-7 px-2 rounded-sm border border-gray-300 text-xs w-48 ml-auto"
                            data-testid={`prov-ean-input-${a.sku}`}
                        />
                        <button type="button" onClick={() => save(a)} disabled={busyId === a.id}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#111827] bg-[#FFCC00] hover:brightness-95 rounded-sm px-2.5 py-1.5 disabled:opacity-60"
                            data-testid={`prov-ean-save-${a.sku}`}>
                            {busyId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Speichern
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};
